import React, { CSSProperties, FC, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { cn } from "@src/lib/utils/cn";

// Looks like Skeleton: the same alpha fill and pulse, instead of the
// library's shimmer (an alpha shimmer stacks on the fill and reads darker).
// Set here so it doesn't depend on a SkeletonTheme. The shimmer is hidden
// through the library's CSS variable rather than enableAnimation={false},
// which would also set aria-busy="false" on the container.
const BASE_COLOR = "var(--color-skeleton)";
const HIDE_SHIMMER_STYLE = {
  "--pseudo-element-display": "none",
} as CSSProperties;
const PULSE_CLASSNAME = "animate-pulse motion-reduce:animate-none";

type Props = {
  height?: CSSProperties["height"];
  width?: CSSProperties["width"];
  style?: CSSProperties;
  className?: string;
  containerClassName?: string;
  visibilityDelayMs?: number;
  /**
   * Corner radius. Set it here, not with a `rounded-*` className: the
   * library's own radius rule wins over Tailwind utilities.
   */
  borderRadius?: CSSProperties["borderRadius"];
};

const DelayedSkeleton: FC<Props> = ({
  height,
  width,
  style,
  className,
  containerClassName,
  visibilityDelayMs = 300,
  borderRadius = 6,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, visibilityDelayMs);
    return () => clearTimeout(timer);
  }, [visibilityDelayMs]);

  if (!isVisible) {
    return null;
  }

  return (
    <Skeleton
      height={height}
      width={width}
      style={{ ...HIDE_SHIMMER_STYLE, ...style }}
      baseColor={BASE_COLOR}
      // Inline, so it beats the library's unlayered 4px radius. The default
      // matches Skeleton's rounded-md.
      borderRadius={borderRadius}
      className={cn(PULSE_CLASSNAME, className)}
      containerClassName={containerClassName}
    />
  );
};

export default DelayedSkeleton;
